import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import {
  TenantsAPI, InstitutionsAPI, YearsAPI, ClassesAPI, SectionsAPI, CampusesAPI,
} from "@/lib/sprint2-api";
import { StudentsAPI } from "@/lib/sprint3-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, GraduationCap, Loader2, Trash2, Search, Upload, Download } from "lucide-react";
import { Link } from "react-router-dom";

export default function Students() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const queryClient = useQueryClient();

  const [tenantId, setTenantId] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [campusId, setCampusId] = useState("all");
  const [classId, setClassId] = useState("all");
  const [sectionId, setSectionId] = useState("all");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState(searchParams.get("q") || "");

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  const effectiveTenantId = isSuper
    ? (tenantId || tenantsQuery.data?.[0]?.id || "")
    : (user?.tenant_id || "");
  const tenantParam = isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : undefined;

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenantId],
    queryFn: () => InstitutionsAPI.list(tenantParam),
    enabled: !!effectiveTenantId,
  });
  const effectiveInstitutionId =
    (institutionsQuery.data?.some((i) => i.id === institutionId) ? institutionId : "") ||
    institutionsQuery.data?.[0]?.id ||
    "";

  const campusesQuery = useQuery({
    queryKey: ["campuses", effectiveInstitutionId],
    queryFn: () => CampusesAPI.list({ institution_id: effectiveInstitutionId, ...(tenantParam || {}) }),
    enabled: !!effectiveInstitutionId,
  });

  const yearsQuery = useQuery({
    queryKey: ["years", effectiveInstitutionId],
    queryFn: () => YearsAPI.list({ institution_id: effectiveInstitutionId, ...(tenantParam || {}) }),
    enabled: !!effectiveInstitutionId,
  });
  const currentYearId = yearsQuery.data?.find((y) => y.is_current)?.id || yearsQuery.data?.[0]?.id;
  const classesQuery = useQuery({
    queryKey: ["classes", currentYearId, campusId],
    queryFn: () => ClassesAPI.list({
      academic_year_id: currentYearId,
      ...(campusId !== "all" ? { campus_id: campusId } : {}),
      ...(tenantParam || {}),
    }),
    enabled: !!currentYearId,
  });

  // Build a campus-id → name map so the Class dropdown can disambiguate when
  // the user is viewing "All campuses" and 9 "Grade 1" entries collapse to a
  // single name. When a specific campus is picked, the labels stay clean.
  const campusName = (cid) => (campusesQuery.data || []).find((c) => c.id === cid)?.name || "";

  // Sections list for the filter bar — depends on the selected class.
  const sectionsQuery = useQuery({
    queryKey: ["sections", classId],
    queryFn: () => SectionsAPI.list({ class_id: classId, ...(tenantParam || {}) }),
    enabled: classId !== "all" && !!classId,
  });

  const studentsQuery = useQuery({
    queryKey: ["students", effectiveInstitutionId, campusId, classId, sectionId, search],
    queryFn: () => StudentsAPI.list({
      institution_id: effectiveInstitutionId,
      ...(campusId !== "all" ? { campus_id: campusId } : {}),
      ...(classId !== "all" ? { class_id: classId } : {}),
      ...(sectionId !== "all" ? { section_id: sectionId } : {}),
      ...(search ? { q: search } : {}),
      ...(tenantParam || {}),
    }),
    enabled: !!effectiveInstitutionId,
  });

  return (
    <AppShell>
      <div className="p-8" data-testid="students-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
<h1 className="font-display font-extrabold tracking-tight text-4xl">Students</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Roster of all enrolled students. Filter by class, search by name
              or admission number.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              data-testid="students-export-csv"
              onClick={async () => {
                try {
                  const params = new URLSearchParams();
                  if (isSuper && effectiveTenantId) params.set("tenant_id", effectiveTenantId);
                  if (effectiveInstitutionId) params.set("institution_id", effectiveInstitutionId);
                  if (campusId && campusId !== "all") params.set("campus_id", campusId);
                  if (classId && classId !== "all") params.set("class_id", classId);
                  if (sectionId && sectionId !== "all") params.set("section_id", sectionId);
                  if (search) params.set("q", search);
                  const { api } = await import("@/lib/api");
                  const res = await api.get(`/students/export.csv?${params.toString()}`, { responseType: "blob" });
                  const blob = new Blob([res.data], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "students.csv";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("Students CSV downloaded");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e) || "Failed to export");
                }
              }}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
            <Link to="/bulk-import?resource=students">
              <Button variant="outline" data-testid="students-bulk-upload">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk upload
              </Button>
            </Link>
            {classId && classId !== "all" && (
              <>
                <Button
                  variant="outline"
                  data-testid="students-print-roster"
                  onClick={async () => {
                    try {
                      const params = new URLSearchParams();
                      if (isSuper && effectiveTenantId) params.set("tenant_id", effectiveTenantId);
                      if (sectionId && sectionId !== "all") params.set("section_id", sectionId);
                      const { api } = await import("@/lib/api");
                      const r = await api.get(`/classes/${classId}/roster.pdf?${params.toString()}`, { responseType: "blob" });
                      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "class-roster.pdf";
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Class roster downloaded");
                    } catch (e) {
                      const msg = e.response?.status === 404 ? "No students in this class yet" : formatApiErrorDetail(e.response?.data?.detail) || "Download failed";
                      toast.error(msg);
                    }
                  }}
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Print Roster
                </Button>
                <Button
                  variant="outline"
                  data-testid="students-roster-csv"
                  onClick={async () => {
                    try {
                      const params = new URLSearchParams();
                      if (isSuper && effectiveTenantId) params.set("tenant_id", effectiveTenantId);
                      if (sectionId && sectionId !== "all") params.set("section_id", sectionId);
                      const { api } = await import("@/lib/api");
                      const r = await api.get(`/classes/${classId}/roster.csv?${params.toString()}`, { responseType: "blob" });
                      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "class-roster.csv";
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Roster CSV downloaded");
                    } catch (e) {
                      const msg = e.response?.status === 404 ? "No students in this class yet" : formatApiErrorDetail(e.response?.data?.detail) || "Download failed";
                      toast.error(msg);
                    }
                  }}
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Roster CSV
                </Button>
              </>
            )}
            <CreateStudent
              institutionId={effectiveInstitutionId}
              currentYearId={currentYearId}
              classes={classesQuery.data || []}
              onDone={() => queryClient.invalidateQueries({ queryKey: ["students"] })}
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5 p-4 bg-card rounded-2xl shadow-sm">
          {isSuper && (
            <div className="space-y-1.5 min-w-[180px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Tenant</Label>
              <Select value={effectiveTenantId} onValueChange={(v) => { setTenantId(v); setInstitutionId(""); setCampusId("all"); setClassId("all"); setSectionId("all"); }}>
                <SelectTrigger data-testid="students-scope-tenant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tenantsQuery.data?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5 min-w-[180px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Institution</Label>
            <Select value={effectiveInstitutionId} onValueChange={(v) => { setInstitutionId(v); setCampusId("all"); setClassId("all"); setSectionId("all"); }}>
              <SelectTrigger data-testid="students-scope-institution"><SelectValue /></SelectTrigger>
              <SelectContent>
                {institutionsQuery.data?.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-[160px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Campus</Label>
            <Select value={campusId} onValueChange={(v) => { setCampusId(v); setClassId("all"); setSectionId("all"); }} disabled={!effectiveInstitutionId}>
              <SelectTrigger data-testid="students-scope-campus"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All campuses</SelectItem>
                {campusesQuery.data?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-[160px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Class</Label>
            <Select value={classId} onValueChange={(v) => { setClassId(v); setSectionId("all"); }}>
              <SelectTrigger data-testid="students-scope-class"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {classesQuery.data?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}{campusId === "all" && c.campus_id ? ` · ${campusName(c.campus_id)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-[140px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Section</Label>
            <Select value={sectionId} onValueChange={setSectionId} disabled={classId === "all"}>
              <SelectTrigger data-testid="students-scope-section"><SelectValue placeholder={classId === "all" ? "—" : "All sections"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sections</SelectItem>
                {sectionsQuery.data?.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 flex-1 min-w-[200px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or admission no."
                className="pl-8"
                data-testid="students-search"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Admission no.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Gender</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {studentsQuery.isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
              ) : !studentsQuery.data?.length ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12">
                  <GraduationCap className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm font-medium">No students yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Add students manually or import a CSV.</p>
                </TableCell></TableRow>
              ) : studentsQuery.data.map((s) => {
                // Prefer server-embedded class/section names (StudentOut now
                // carries them). Fallback to the client-side classes list for
                // any older cached rows that don't have them.
                const clsName = s.class_name || classesQuery.data?.find((c) => c.id === s.class_id)?.name;
                return (
                  <TableRow
                    key={s.id}
                    data-testid={`student-row-${s.admission_no}`}
                    className="cursor-pointer hover:bg-muted/40 transition"
                    onClick={() => navigate(`/students/${s.id}`)}
                  >
                    <TableCell className="font-mono text-xs">{s.admission_no}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div
                          className="h-8 w-8 rounded-full overflow-hidden ring-1 ring-border bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center text-[10px] font-semibold text-primary shrink-0"
                          style={s.photo_url ? { backgroundImage: `url(${s.photo_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                          data-testid={`student-avatar-${s.admission_no}`}
                        >
                          {!s.photo_url && `${(s.first_name || " ")[0]}${(s.last_name || " ")[0]}`.toUpperCase()}
                        </div>
                        <div className="font-medium hover:text-primary transition">{s.first_name} {s.last_name}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground capitalize">{s.gender || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {clsName ? `${clsName}${s.section_name ? " · " + s.section_name : ""}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.contact_phone || s.contact_email || "—"}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" onClick={async () => {
                        if (!window.confirm("Soft-delete this student?")) return;
                        try { await StudentsAPI.remove(s.id); toast.success("Student removed"); queryClient.invalidateQueries({ queryKey: ["students"] }); }
                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      }} data-testid={`student-delete-${s.admission_no}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppShell>
  );
}

function CreateStudent({ institutionId, currentYearId, classes, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", admission_no: "",
    gender: "", dob: "", class_id: "", section_id: "",
    contact_phone: "", contact_email: "", address: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sectionsQuery = useQuery({
    queryKey: ["sections", form.class_id],
    queryFn: () => SectionsAPI.list({ class_id: form.class_id }),
    enabled: !!form.class_id,
  });

  const submit = async (e) => {
    e.preventDefault();
    // JS-level guards for all four required fields (native `required` on the
    // shadcn <Select /> triggers doesn't participate in form validation, and
    // we drop native `required` on inputs too so all four errors surface
    // through the same inline error box).
    if (!form.first_name.trim()) return setError("First name is required");
    if (!form.contact_phone.trim()) return setError("Phone is required");
    if (!form.class_id) return setError("Class is required");
    if (!form.section_id) return setError("Section is required");
    setError(""); setBusy(true);
    try {
      await StudentsAPI.create({
        institution_id: institutionId,
        ...form,
        // trim optional fields so blank strings don't get persisted
        last_name: form.last_name?.trim() || undefined,
        admission_no: form.admission_no?.trim() || undefined,
        contact_email: form.contact_email?.trim() || undefined,
        gender: form.gender || undefined,
        dob: form.dob || undefined,
      });
      toast.success("Student created");
      setForm({ first_name: "", last_name: "", admission_no: "", gender: "", dob: "", class_id: "", section_id: "", contact_phone: "", contact_email: "", address: "" });
      setOpen(false);
      onDone?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={!institutionId} data-testid="create-student-button">
          <Plus className="h-4 w-4 mr-1.5" /> Student
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold tracking-tight text-2xl">New student</DialogTitle>
          <DialogDescription>Roll a new pupil onto your roster.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="student-form">
          <div className="grid grid-cols-2 gap-3">
            <F label="First name *"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="student-form-first-name" /></F>
            <F label="Last name"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="student-form-last-name" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Admission no. (optional)"><Input value={form.admission_no} onChange={(e) => setForm({ ...form, admission_no: e.target.value })} placeholder="Auto-generated if blank" className="font-mono" data-testid="student-form-admission" /></F>
            <F label="DOB"><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="student-form-dob" /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Gender">
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="student-form-gender"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Class *">
              <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v, section_id: "" })}>
                <SelectTrigger data-testid="student-form-class"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Section *">
              <Select value={form.section_id} onValueChange={(v) => setForm({ ...form, section_id: v })} disabled={!form.class_id}>
                <SelectTrigger data-testid="student-form-section"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {sectionsQuery.data?.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Phone *"><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+91 9999999999" data-testid="student-form-phone" /></F>
            <F label="Email"><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="student-form-email" /></F>
          </div>
          {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="student-form-error">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="student-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
